import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { isElectron } from 'utils/environment';
import { trackError } from 'utils/trackError';

export type UpdateStatus =
  'idle' | 'checking' | 'available' | 'not-available' | 'downloading' | 'downloaded' | 'error';

interface UpdateInfo {
  version: string;
  releaseDate?: string;
  releaseNotes?: string;
}

interface UpdateProgress {
  percent: number;
  transferred: number;
  total: number;
}

export function useAutoUpdate({ enabled = true }: { enabled?: boolean } = {}) {
  const { t } = useTranslation();
  const isDev = typeof window !== 'undefined' && window.electronAPI?.isDev;
  const isManagedInstall =
    typeof window !== 'undefined' && Boolean(window.electronAPI?.isManagedInstall);
  const [status, setStatus] = useState<UpdateStatus>('idle');
  const [currentVersion, setCurrentVersion] = useState<string>('');
  const [updateInfo, setUpdateInfo] = useState<UpdateInfo | null>(null);
  const [downloadProgress, setDownloadProgress] = useState<UpdateProgress | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isElectron || isDev) return;

    window.electronAPI.update
      .getCurrentVersion()
      .then((result) => {
        setCurrentVersion(result.version);
      })
      .catch((err) => {
        console.error('Failed to get current version:', err);
      });
  }, [isDev]);

  const handleUpdateError = useCallback(
    (error: unknown, context: { action: string; [key: string]: unknown }) => {
      const fullError = error instanceof Error ? error : new Error(String(error));
      const errorMessage = fullError.message || 'Unknown error occurred';

      if (!errorMessage.startsWith('Auto-updates disabled')) {
        trackError(fullError, {
          component: 'useAutoUpdate',
          ...context,
          errorMessage,
          errorStack: fullError.stack,
        });
      }

      return t('updates.errorGeneric');
    },
    [t]
  );

  const handleDownload = useCallback(async () => {
    if (!isElectron) {
      toast.error(t('updates.onlyInDesktopApp'));
      return;
    }

    setStatus('downloading');
    setError(null);

    try {
      const result = await window.electronAPI.update.download();
      if (!result.success && result.error) {
        const userMessage = handleUpdateError(result.error, {
          action: 'download',
          resultError: result.error,
        });
        setStatus('error');
        setError(userMessage);
        toast.error(t('updates.downloadFailed'), {
          description: userMessage,
        });
      }
    } catch (err) {
      const userMessage = handleUpdateError(err, {
        action: 'download',
      });
      setStatus('error');
      setError(userMessage);
      toast.error(t('updates.downloadFailed'), {
        description: userMessage,
      });
    }
  }, [t, handleUpdateError]);

  const handleInstall = useCallback(async () => {
    if (!isElectron) {
      toast.error(t('updates.onlyInDesktopApp'));
      return;
    }

    try {
      const result = await window.electronAPI.update.install();
      if (!result.success && result.error) {
        const userMessage = handleUpdateError(result.error, {
          action: 'install',
          resultError: result.error,
        });
        toast.error(t('updates.installFailed'), {
          description: userMessage,
        });
      }
      // Note: install() will restart the app, so we won't see the result
    } catch (err) {
      const userMessage = handleUpdateError(err, {
        action: 'install',
      });
      toast.error(t('updates.installFailed'), {
        description: userMessage,
      });
    }
  }, [t, handleUpdateError]);

  const handleCheck = useCallback(async () => {
    if (!isElectron) {
      return;
    }

    setStatus('checking');
    setError(null);

    try {
      const result = await window.electronAPI.update.check();
      if (!result.success && result.error) {
        const userMessage = handleUpdateError(result.error, {
          action: 'check',
          resultError: result.error,
        });
        setStatus('error');
        setError(userMessage);
      }
    } catch (err) {
      const userMessage = handleUpdateError(err, {
        action: 'check',
      });
      setStatus('error');
      setError(userMessage);
    }
  }, [handleUpdateError]);

  useEffect(() => {
    if (!enabled || !isElectron || isDev || isManagedInstall) return;
    void handleCheck();
  }, [enabled, handleCheck, isDev, isManagedInstall]);

  // Set up event listeners
  useEffect(() => {
    if (!isElectron || isDev || isManagedInstall) return;

    const unsubscribers: Array<() => void> = [];

    unsubscribers.push(
      window.electronAPI.update.onChecking(() => {
        setStatus('checking');
        setError(null);
      })
    );

    unsubscribers.push(
      window.electronAPI.update.onAvailable((info) => {
        setStatus('available');
        setUpdateInfo(info);
        toast.info(t('updates.available', { version: info.version }), {
          description: t('updates.downloadingInBackground'),
        });
        void handleDownload();
      })
    );

    unsubscribers.push(
      window.electronAPI.update.onNotAvailable((info) => {
        setStatus('not-available');
        setUpdateInfo({ version: info.version });
      })
    );

    unsubscribers.push(
      window.electronAPI.update.onError((err) => {
        const userMessage = handleUpdateError(err, {
          action: 'update-event',
          errorMessage: err.message,
        });
        setStatus('error');
        setError(userMessage);
        toast.error(t('updates.updateError'), {
          description: userMessage,
        });
      })
    );

    unsubscribers.push(
      window.electronAPI.update.onDownloadProgress((progress) => {
        setStatus('downloading');
        setDownloadProgress(progress);
      })
    );

    unsubscribers.push(
      window.electronAPI.update.onDownloaded((info) => {
        setStatus('downloaded');
        setUpdateInfo(info);
        toast.success(t('updates.downloaded'), {
          description: t('updates.readyToInstall', { version: info.version }),
          action: {
            label: t('updates.installAndRestart'),
            onClick: () => {
              void handleInstall();
            },
          },
          duration: 10000,
        });
      })
    );

    return () => {
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [handleDownload, handleInstall, t, handleUpdateError, isDev, isManagedInstall]);

  return {
    status,
    currentVersion,
    updateInfo,
    downloadProgress,
    error,
    isElectron,
    isDev,
    isManagedInstall,
    checkForUpdates: handleCheck,
    downloadUpdate: handleDownload,
    installUpdate: handleInstall,
  };
}
